import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  CheckboxGrid,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getRnSupVisitForm,
  updateRnSupVisitForm,
} from "../../../../../services/patientService";
import {
  FileCheckIcon,
  HeartPulseIcon,
  NurseIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  RN_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  COMMUNICATION_METHOD_OPTIONS,
  COMMUNICATION_WITH_OPTIONS,
  EDUCATION_RESPONSE_OPTIONS,
  EDUCATION_TOPIC_OPTIONS,
  FUNCTIONAL_STATUS_OPTIONS,
  GENERAL_APPEARANCE_OPTIONS,
  REQUIREMENTS_CHECKLIST_OPTIONS,
  SKIN_INTEGRITY_OPTIONS,
  YES_NO_OPTIONS,
  emptyRnSupVisitPayload,
  toggleInList,
  today,
} from "./rnSupVisitContent";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "./nurseVisitCommon";

import NurseNotesCommonHeader from "./NurseNotesCommonHeader";
import ClinicalVitalsFields from "./ClinicalVitalsFields";
import WoundPresentSection from "./WoundPresentSection";
import { applyWoundAssessmentPayload } from "./woundAssessmentContent";
import {
  normalizeClinicalVitals,
  withCanonicalClinicalVitals,
} from "./clinicalVitals";
import {
  normalizeCommonHeaderState,
  resolveNurseNotesCommonHeader,
  withNormalizedCommonHeader,
} from "./nurseNotesCommonHeaderUtils";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const ARRAY_KEYS = [
  "participants",
  "requirementsChecklist",
  "generalAppearance",
  "skinIntegrity",
  "functionalStatus",
  "educationTopics",
  "educationResponse",
  "communicationWith",
  "communicationMethod",
];

const emptyValues = () => ({
  ...emptyRnSupVisitPayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});


const RadioRow = ({ options, value, canEdit, onChange, name }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
    {options.map((opt) => (
      <label
        key={opt}
        className={`inline-flex items-center gap-1.5 text-[12px] text-slate-700 ${
          canEdit ? "cursor-pointer" : "cursor-default"
        }`}
      >
        <input
          type="radio"
          name={name}
          className="h-3.5 w-3.5 border-slate-300 text-indigo-500 focus:ring-indigo-200"
          checked={value === opt}
          disabled={!canEdit}
          onChange={() => onChange(opt)}
        />
        <span>{opt}</span>
      </label>
    ))}
  </div>
);

RadioRow.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
};

const RnSupVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const normalizeLoadedPayload = useCallback((loadedPayload) => {
    const base = emptyRnSupVisitPayload();
    const merged = { ...base, ...loadedPayload };
    ARRAY_KEYS.forEach((key) => {
      merged[key] = Array.isArray(loadedPayload[key]) ? loadedPayload[key] : [];
    });
    merged.commonHeader = normalizeCommonHeaderState(loadedPayload.commonHeader);
    if (!merged.visitLocation) merged.visitLocation = "Patient Home";
    Object.assign(merged, normalizeClinicalVitals(merged));
    applyWoundAssessmentPayload(merged, merged, {
      mrn: patient?.mrn,
    });
    return merged;
  }, [patient?.mrn]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      setValues({
        ...normalizeLoadedPayload(loadedPayload),
        ...pickNurseVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [normalizeLoadedPayload, patientName, resetSignatures],
  );

  const buildPatientPrefill = useCallback(() => {
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];

    return {
      ...emptyRnSupVisitPayload(),
      mrn: patient?.mrn || "",
      certPeriodFrom: latestEpisode?.socDate || latestEpisode?.startDate
        ? formatDateForInput(latestEpisode?.socDate || latestEpisode?.startDate)
        : "",
      certPeriodTo: latestEpisode?.endDate || latestEpisode?.toDate
        ? formatDateForInput(latestEpisode?.endDate || latestEpisode?.toDate)
        : "",
    };
  }, [patient]);

  const mergePatientSnapshot = useCallback(
    (existingPayload) => {
      const prefill = buildPatientPrefill();
      return {
        ...prefill,
        ...existingPayload,
        mrn: existingPayload.mrn || prefill.mrn || "",
        certPeriodFrom:
          existingPayload.certPeriodFrom || prefill.certPeriodFrom || "",
        certPeriodTo: existingPayload.certPeriodTo || prefill.certPeriodTo || "",
      };
    },
    [buildPatientPrefill],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveNurseNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName || draft?.formName || "Nurse Supervisory Visit",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getRnSupVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveNurseNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...mergePatientSnapshot(existingPayload), commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        RN_FORM_EDITOR_ROLES,
        "View only — only RN/LVN can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      visitDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...rnFields
    } = values;

    const nestedPayload = withCanonicalClinicalVitals(
      withNormalizedCommonHeader({
        ...rnFields,
        dateOfBirth: values.dateOfBirth ? formatDateForInput(values.dateOfBirth) : "",
        certPeriodFrom: values.certPeriodFrom
          ? formatDateForInput(values.certPeriodFrom)
          : "",
        certPeriodTo: values.certPeriodTo
          ? formatDateForInput(values.certPeriodTo)
          : "",
        visitDate: visitDate ? formatDateForInput(visitDate) : "",
        commonHeader: values.commonHeader,
      }),
    );

    ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateRnSupVisitForm,
        filledByUserId,
        scheduleDate: values.visitDate
          ? formatDateForInput(values.visitDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Nurse Supervisory Visit
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            CMS supervisory visit documentation for home health services.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || values.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              values.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Date"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <NurseNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />



        <Section
          icon={ShieldCheckIcon}
          title="7. Supervisory Visit Requirements (CMS 42 CFR §484.80)"
        >
          <p className="text-[11px] text-slate-500 italic mb-2">
            The supervising nurse must perform an on-site visit and follow these
            requirements.
          </p>
          <CheckboxGrid
            options={REQUIREMENTS_CHECKLIST_OPTIONS}
            values={values.requirementsChecklist}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("requirementsChecklist", opt)}
            columns={2}
          />
        </Section>

        <Section icon={HeartPulseIcon} title="8. Clinical Vitals & Trends">
          <ClinicalVitalsFields
            values={values}
            canEdit={canEdit}
            onChange={(key, next) => setValue(key, next)}
          />
        </Section>

        <Section icon={NurseIcon} title="Wound Assessment (If Applicable)">
          <WoundPresentSection
            values={values}
            canEdit={canEdit}
            printMode={printMode}
            onChange={(key, next) => setValue(key, next)}
            onToggleList={(key, opt) => toggleListValue(key, opt)}
          />
        </Section>

        <Section icon={HeartPulseIcon} title="9. Clinical Assessment">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-3">
            <div className="space-y-3">
              <div>
                <SubHeading>General Appearance</SubHeading>
                <CheckboxGrid
                  options={GENERAL_APPEARANCE_OPTIONS}
                  values={values.generalAppearance}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("generalAppearance", opt)}
                  columns={2}
                />
                {values.generalAppearance.includes("Other") ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.generalAppearanceOther}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("generalAppearanceOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
              <div>
                <SubHeading>Skin Integrity</SubHeading>
                <CheckboxGrid
                  options={SKIN_INTEGRITY_OPTIONS}
                  values={values.skinIntegrity}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("skinIntegrity", opt)}
                  columns={2}
                />
                {values.skinIntegrity.includes("Other") ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.skinIntegrityOther}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("skinIntegrityOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <SubHeading>Functional Status</SubHeading>
                <CheckboxGrid
                  options={FUNCTIONAL_STATUS_OPTIONS}
                  values={values.functionalStatus}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("functionalStatus", opt)}
                  columns={1}
                />
              </div>
              <Field label="Comments">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.clinicalComments}
                  disabled={!canEdit}
                  onChange={(e) => setValue("clinicalComments", e.target.value)}
                />
              </Field>
              <div>
                <SubHeading>Patient/Caregiver Education Provided</SubHeading>
                <CheckboxGrid
                  options={EDUCATION_TOPIC_OPTIONS}
                  values={values.educationTopics}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("educationTopics", opt)}
                  columns={2}
                />
                {values.educationTopics.includes("Other") ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.educationOther}
                    disabled={!canEdit}
                    onChange={(e) => setValue("educationOther", e.target.value)}
                  />
                ) : null}
              </div>
              <div>
                <SubHeading>Response to Education</SubHeading>
                <CheckboxGrid
                  options={EDUCATION_RESPONSE_OPTIONS}
                  values={values.educationResponse}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("educationResponse", opt)}
                  columns={1}
                />
              </div>
            </div>
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="10. Plan of Care Review">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <SubHeading>Plan of Care Reviewed</SubHeading>
              <RadioRow
                name="planOfCareReviewed"
                options={YES_NO_OPTIONS}
                value={values.planOfCareReviewed}
                canEdit={canEdit}
                onChange={(v) => setValue("planOfCareReviewed", v)}
              />
            </div>
            <div>
              <SubHeading>Plan of Care Remains Appropriate</SubHeading>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <RadioRow
                  name="planRemainsAppropriate"
                  options={YES_NO_OPTIONS}
                  value={values.planRemainsAppropriate}
                  canEdit={canEdit}
                  onChange={(v) => setValue("planRemainsAppropriate", v)}
                />
                <span className="text-[11px] italic text-slate-500">
                  (If No, see revisions below)
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Revisions to Plan of Care">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.planRevisions}
                disabled={!canEdit}
                onChange={(e) => setValue("planRevisions", e.target.value)}
              />
            </Field>
            <Field label="New Orders/Instructions">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.newOrdersInstructions}
                disabled={!canEdit}
                onChange={(e) => setValue("newOrdersInstructions", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Follow-up Required</SubHeading>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <RadioRow
                name="followUpRequired"
                options={YES_NO_OPTIONS}
                value={values.followUpRequired}
                canEdit={canEdit}
                onChange={(v) => setValue("followUpRequired", v)}
              />
              {values.followUpRequired === "Yes" ? (
                <input
                  type="text"
                  className={`${fieldClass} max-w-md`}
                  value={values.followUpSpecify}
                  disabled={!canEdit}
                  placeholder="If Yes, specify…"
                  onChange={(e) => setValue("followUpSpecify", e.target.value)}
                />
              ) : null}
            </div>
          </div>
        </Section>

        <Section icon={PhoneIcon} title="11. Communication">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-3">
            <div className="space-y-3">
              <div>
                <SubHeading>Communication with</SubHeading>
                <CheckboxGrid
                  options={COMMUNICATION_WITH_OPTIONS}
                  values={values.communicationWith}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("communicationWith", opt)}
                  columns={2}
                />
              </div>
              <div>
                <SubHeading>Method</SubHeading>
                <CheckboxGrid
                  options={COMMUNICATION_METHOD_OPTIONS}
                  values={values.communicationMethod}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("communicationMethod", opt)}
                  columns={3}
                />
              </div>
              <Field label="Other">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.communicationOther}
                  disabled={!canEdit}
                  onChange={(e) => setValue("communicationOther", e.target.value)}
                />
              </Field>
            </div>
            <Field label="Summary of Communication">
              <textarea
                rows={8}
                className={textareaClass}
                value={values.communicationSummary}
                disabled={!canEdit}
                onChange={(e) => setValue("communicationSummary", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

RnSupVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default RnSupVisitForm;
